// @ts-ignore
import {HttpClient} from '@angular/common/http';
// @ts-ignore
import {Injectable} from '@angular/core';
// @ts-ignore
import {firstValueFrom} from 'rxjs';
// @ts-ignore
import {SankhyaGatewayService} from './sankhya-gateway.service';
import {HomeLogoAnimationService} from './home-logo-animation.service';

// @ts-ignore
@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly apiUrl = '/api/sankhya';

    // CODUSU numérico resolvido (ver getCodusuNumerico) — cacheado pro resto da sessão.
    private codusuCache: number | null = null;
    private codusuPromise: Promise<number> | null = null;

    constructor(
        private readonly http: HttpClient,
        private readonly gateway: SankhyaGatewayService,
        private readonly homeLogoAnimation: HomeLogoAnimationService
    ) {
    }

    async loginLocalAdmin(_username: string, _password: string): Promise<{ success: boolean; data?: unknown }> {
        return {success: false};
    }

    async loginRemoteApi(username: string, password: string, manterLogado: boolean = true): Promise<any> {
        const response = await firstValueFrom(this.http.post<any>(`${this.apiUrl}/login`, {
            username,
            password,
            keepConnected: manterLogado ? 'Y' : 'N'
        }));

        if (response?.authenticated !== true) {
            throw new Error('Login negado pelo Sankhya.');
        }

        const usuarioInfo = {
            usuarioNome: username.toUpperCase(),
            // O login retorna o ambiente da instancia, nao o grupo do usuario.
            // O grupo real e descoberto pelo servico de permissoes do menu.
            grupoNome: '',
            usuarioId: username
        };

        // Garante que nenhum CODUSU de uma sessão anterior (outro usuário) fique
        // em cache: sem isso, telas como Separação/Conferência de Estoque continuavam
        // filtrando pelos dados do usuário anterior após a troca de login.
        this.codusuCache = null;
        this.codusuPromise = null;

        const storage = manterLogado ? localStorage : sessionStorage;

        storage.setItem('isUserAuthenticated', 'true');
        storage.setItem('isAdminAuthenticated', 'false');
        storage.setItem('userName', usuarioInfo.usuarioNome);
        storage.setItem('userGroup', usuarioInfo.grupoNome);
        storage.setItem('userId', usuarioInfo.usuarioId);
        storage.setItem('sankhyaUsername', username);

        localStorage.setItem('authStorageType', manterLogado ? 'local' : 'session');
        this.homeLogoAnimation.resetForLogin();

        return {
            authenticated: true,
            usuarioInfo,
            gatewayLogin: response
        };
    }

    async fetchAllCompanies(): Promise<any[]> {
        return [];
    }

    isAuthenticated(): boolean {
        const storage = this.getAuthStorage();

        return storage.getItem('isUserAuthenticated') === 'true';
    }

    getUserName(): string {
        return this.getAuthStorage().getItem('userName') || '';
    }

    getUserGroup(): string {
        return this.getAuthStorage().getItem('userGroup') || '';
    }

    getUserId(): string {
        return this.getAuthStorage().getItem('userId') || '';
    }

    /**
     * Resolve o CODUSU numérico do usuário logado no Sankhya.
     *
     * A sessão guarda em "userId"/"sankhyaUsername" o USERNAME de login (string,
     * ex.: "jsilva"), não o código numérico do usuário no Sankhya (TSIUSU.CODUSU).
     * Telas que precisam filtrar "só o que é meu" (Separação de Estoque, Conferência,
     * Pendências do Vendedor) precisam desse número — usar `Number(getUserId())` sempre
     * dava NaN→0, fazendo essas telas caírem no modo "ver tudo" (codusu=0) mesmo pra
     * usuários comuns.
     *
     * Fonte primária: pergunta pro backend "quem sou eu" (buscarMeuCodusu), resolvido
     * pela sessão real do Sankhya (AuthenticationInfo) — a MESMA fonte que o backend usa
     * quando grava iniciar/transferir/dividir separação, então front e backend nunca
     * discordam. Isso é importante especialmente pro usuário SUP: o fallback antigo
     * (buscar por nome em TSIUSU) tem "AND CODUSU > 0" na query e por isso NUNCA
     * encontra o SUP (CODUSU=0) — o front ficava calculando um CODUSU errado (ou
     * coincidentemente de outro usuário cujo nome contém "SUP") só pro SUP, fazendo a
     * tela de Separação mostrar tudo errado só pra essa conta. Mantém o fallback por
     * nome só pra caso o endpoint novo falhe por algum motivo.
     */
    async getCodusuNumerico(): Promise<number> {
        if (this.codusuCache !== null) {
            return this.codusuCache;
        }
        if (this.codusuPromise) {
            return this.codusuPromise;
        }

        const bruto = (this.getAuthStorage().getItem('sankhyaUsername') || this.getUserId() || '').trim();
        if (/^\d+$/.test(bruto)) {
            this.codusuCache = Number(bruto);
            return this.codusuCache;
        }

        this.codusuPromise = firstValueFrom(this.gateway.buscarMeuCodusu())
            .then((res: any) => {
                if (res?.sucesso) {
                    const codusu = Number(res.codusu) || 0;
                    this.codusuCache = codusu;
                    return codusu;
                }
                return this.resolverCodusuPorNomeTsiusu(bruto);
            })
            .catch(() => this.resolverCodusuPorNomeTsiusu(bruto))
            .finally(() => { this.codusuPromise = null; });

        return this.codusuPromise;
    }

    /**
     * Empresa "padrão" do usuário logado (TSIUSU.CODEMP nativo), resolvida pelo
     * mesmo endpoint "quem sou eu" usado por getCodusuNumerico(). Usada só pelo
     * EmpresaStateService pra pré-selecionar a empresa no primeiro acesso deste
     * navegador — o usuário continua podendo trocar manualmente depois.
     * Retorna null se o usuário não tiver empresa padrão definida no Sankhya,
     * ou se o endpoint falhar por qualquer motivo — nesses casos quem chama
     * deve cair no fallback de sempre (primeira empresa da lista).
     */
    async getCodEmpUsuario(): Promise<number | null> {
        try {
            const res: any = await firstValueFrom(this.gateway.buscarMeuCodusu());
            const codEmp = Number(res?.codEmpUsuario) || 0;
            return codEmp > 0 ? codEmp : null;
        } catch {
            return null;
        }
    }

    /** Fallback: busca por nome exato em TSIUSU. Não encontra CODUSU=0 (SUP) — só
     *  usado se buscarMeuCodusu() falhar por algum motivo (ex.: addon fora do ar). */
    private async resolverCodusuPorNomeTsiusu(bruto: string): Promise<number> {
        if (!bruto) {
            this.codusuCache = 0;
            return 0;
        }
        const alvo = bruto.toUpperCase();
        try {
            const res: any = await firstValueFrom(this.gateway.buscarUsuariosSeparacao(bruto));
            const lista: any[] = res?.lista ?? [];
            const match = lista.find((u: any) => String(u?.nomeusu ?? '').trim().toUpperCase() === alvo);
            const codusu = match ? Number(match.codusu) || 0 : 0;
            this.codusuCache = codusu;
            return codusu;
        } catch {
            this.codusuCache = 0;
            return 0;
        }
    }

    clearSession(): void {
        this.codusuCache = null;
        this.codusuPromise = null;
        localStorage.removeItem('token');
        localStorage.removeItem('isUserAuthenticated');
        localStorage.removeItem('isAdminAuthenticated');
        localStorage.removeItem('userName');
        localStorage.removeItem('userGroup');
        localStorage.removeItem('userId');
        localStorage.removeItem('activeCompanyId');
        localStorage.removeItem('activeCompanyName');
        localStorage.removeItem('sankhyaUsername');
        localStorage.removeItem('sankhyaPassword');
        localStorage.removeItem('authStorageType');

        sessionStorage.removeItem('token');
        sessionStorage.removeItem('isUserAuthenticated');
        sessionStorage.removeItem('isAdminAuthenticated');
        sessionStorage.removeItem('userName');
        sessionStorage.removeItem('userGroup');
        sessionStorage.removeItem('userId');
        sessionStorage.removeItem('activeCompanyId');
        sessionStorage.removeItem('activeCompanyName');
        sessionStorage.removeItem('sankhyaUsername');
        sessionStorage.removeItem('sankhyaPassword');
    }

    authErrorMessage(error: any): string {
        if (error?.status === 0) {
            return 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.';
        }
        if ([502, 503, 504].includes(error?.status)) {
            return 'O serviço de login está indisponível no momento. Tente novamente em instantes.';
        }
        if (error?.status === 404) {
            return 'Serviço de login não encontrado. Verifique a configuração do servidor.';
        }
        const detail = error?.error?.detail || error?.error?.message;
        if (typeof detail === 'string' && detail.trim()) {
            return detail;
        }
        if (error?.status === 401 || error?.status === 403) {
            return 'Usuário ou senha inválidos.';
        }
        return 'Não foi possível entrar. Tente novamente em instantes.';
    }

    private getAuthStorage(): Storage {
        const tipo = localStorage.getItem('authStorageType');

        if (tipo === 'session') {
            return sessionStorage;
        }

        return localStorage;
    }
}
